/**
 * Event Sharing Utility
 *
 * Generates shareable event URLs and handles native sharing via Web Share API.
 *
 * @module shareEvent
 */

import { ROUTES } from 'router/routes';

/**
 * Generates a shareable event URL
 *
 * @param eventKey - The event key/identifier
 * @param isConcert - Whether this is a concert event (uses different route)
 * @returns The shareable URL
 */
/** `true`/`false` keep the legacy concert/match routes; `'international'` uses the detail route. */
export type EventShareKind = boolean | 'international';

export const generateEventUrl = (eventKey: string, kind: EventShareKind = false): string => {
  const route =
    kind === 'international'
      ? ROUTES.internationalEvent
      : kind
      ? ROUTES.singleConcert
      : ROUTES.event;
  const cleanRoute = route.startsWith('/') ? route.slice(1) : route;
  const path = cleanRoute.replace(':eventKey', eventKey);
  const fullPath = path.startsWith('/') ? path : `/${path}`;

  return `https://tickets.digitalzone.app${fullPath}`;
};

/**
 * Shares an event using native Web Share API
 *
 * @param eventName - The name/title of the event
 * @param eventKey - The event key/identifier
 * @param isConcert - Whether this is a concert event (uses different route)
 */
export const shareEvent = async (
  eventName: string,
  eventKey: string,
  kind: EventShareKind = false,
): Promise<void> => {
  const url = generateEventUrl(eventKey, kind);
  const text = `تصفح الحدث: ${eventName}`;

  // Use navigator.share if available
  if (navigator.share) {
    await navigator.share({
      title: eventName,
      text,
      url,
    });
    return;
  }

  // Fallback: Copy to clipboard
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    throw new Error('COPIED'); // Special error to indicate success
  }

  throw new Error('Sharing not available');
};
